/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_rec_arr, t_key, t_rec, t_arr } from '../types';
import fold, { t_stop_fn } from './fold';
import mimic from '../fn/mimic';
import is_array from '../is/is_array';

export type t_filter_fn<V> = (v: V, k: t_key, adt: t_rec_arr<V>, stop: t_stop_fn) => boolean;

export interface Filter {
  /**
   * @public
   * @function f1.filter
   * @param fn the boolean function to decide if each value is included
   * @param adt the array or record we will filter
   * @example
   *
   * ```ts
   * f1.filter(v => v > 16, [16, 17, 18]) === [17, 18]
   * ```
   *
   */
  <V>(fn: t_filter_fn<V>, adt: t_arr<V>): t_arr<V>;
  <V>(fn: t_filter_fn<V>, adt: t_rec<string, V>): t_rec<string, V>;
  <V>(fn: t_filter_fn<V>, adt: t_rec_arr<V>): t_rec_arr<V>;

  <V>(fn: t_filter_fn<V>): (adt: t_arr<V>) => t_arr<V>;
  <V>(fn: t_filter_fn<V>): (adt: t_rec<string, V>) => t_rec<string, V>;
  <V>(fn: t_filter_fn<V>): (adt: t_rec_arr<V>) => t_rec_arr<V>;
}

/**
 * @see Filter
 */
export default <V>(fn: t_filter_fn<V>, adt: t_rec_arr<V>): t_rec_arr<V> => {
  const next = mimic(adt);
  const isArray = is_array(next);

  return fold<V, t_rec_arr<V>, t_rec_arr<V>>(
    (acc, v, k, adt, stop) => {
      if (fn(v, k, adt, stop)) {
        // @note we use a pre-calculated variable for performance
        if (isArray) {
          (acc as t_arr<V>).push(v);
        } else {
          acc[k] = v;
        }
      }
      return acc;
    },
    next,
    adt
  );
};
